/**
 * Wat een tegel of lijstregel in de pensionoverzichten laat zien, en hoe de balie het wil hebben: welke weergave,
 * gegroepeerd of niet, en in welke volgorde.
 *
 * <p>Gegroepeerd op reservering staat de klant al in de kop van de groep; los staat elk dier op zichzelf, en dan
 * hoort de klant in de tegel. Daarom heeft elke groepering zijn eigen velden.
 */

export type Onderdeel = "PENSION" | "DAGOPVANG" | "RESERVERINGEN" | "BEZETTING";
export type Tab = "aanwezig" | "aankomsten" | "vertrekken" | "reserveringen";
export type Status = "GEPLAND" | "KOMT" | "AANWEZIG" | "VERTREKT" | "VERTROKKEN";
export type Weergave = "GROOT" | "KLEIN" | "LIJST";
export type Groeperen = "GEEN" | "RESERVERING";
export type Sortering = "AANKOMST_NIEUW" | "AANKOMST_OUD" | "VERTREK" | "NAAM" | "EIGENAAR" | "KENNEL";

export interface Regel {
  soort: "PENSION" | "DAGOPVANG";
  regelId: number | null;
  reserveringId: number | null;
  reserveringNummer: number | null;
  dierId: number;
  dierNaam: string | null;
  diersoort: string | null;
  ras: string | null;
  geslacht: string | null;
  gecastreerd: boolean;
  klantId: number | null;
  klantNaam: string | null;
  achternaam: string | null;
  email: string | null;
  telefoon: string | null;
  woonplaats: string | null;
  kennel: string | null;
  van: string;
  tot: string;
  nachten: number;
  aankomst: boolean;
  vertrek: boolean;
  status: Status;
  voeding: string | null;
  medicatie: boolean;
  medicatieInstructies: string | null;
  /** Wat er bij dit verblijf over het dier is vastgelegd, en anders de bijzonderheden van de dierkaart. */
  bijzonderheden: string | null;
  brengen: string | null;
  halen: string | null;
  /** De pensionopmerking: de opmerking op de reservering, voor alle dieren erop dezelfde. */
  opmerking: string | null;
}

export interface Overzicht {
  vestigingId: number;
  datum: string;
  tot: string;
  telling: { aanwezig: number; aankomsten: number; vertrekken: number };
  regels: Regel[];
}

/** De velden die de balie aan en uit kan zetten, in de volgorde waarin ze op de tegel staan. */
export const VELDEN = [
  "verblijfnr",
  "status",
  "ras",
  "eigenaar",
  "telefoon",
  "email",
  "woonplaats",
  "reservering",
  "kennel",
  "verblijf",
  "nachten",
  "voeding",
  "medicatie",
  "bijzonderheden",
  "opmerking",
] as const;
export type Veld = (typeof VELDEN)[number];

export const STANDAARD_VELDEN: Record<Groeperen, Veld[]> = {
  RESERVERING: ["verblijfnr", "status", "ras", "kennel", "verblijf", "nachten", "voeding", "medicatie", "opmerking"],
  GEEN: ["verblijfnr", "status", "ras", "eigenaar", "telefoon", "kennel", "verblijf", "nachten", "voeding", "medicatie", "opmerking"],
};

export interface Voorkeur {
  weergave: Weergave;
  groeperen: Groeperen;
  sortering: Sortering;
  velden: Record<Groeperen, Veld[]>;
}

export const STANDAARD_VOORKEUR: Voorkeur = {
  weergave: "KLEIN",
  groeperen: "RESERVERING",
  sortering: "AANKOMST_NIEUW",
  velden: STANDAARD_VELDEN,
};

const OPSLAG = "fawkes.pensionOverzichten.voorkeur";

/** Wat deze balie de vorige keer koos; een privévenster of geblokkeerde opslag geeft gewoon de standaard. */
export function leesVoorkeur(): Voorkeur {
  try {
    const ruw = window.localStorage.getItem(OPSLAG);
    if (!ruw) return STANDAARD_VOORKEUR;
    const v = JSON.parse(ruw) as Partial<Voorkeur>;
    const geldig = (lijst: unknown, standaard: Veld[]) =>
      Array.isArray(lijst) ? lijst.filter((x): x is Veld => (VELDEN as readonly string[]).includes(x)) : standaard;
    return {
      weergave: v.weergave === "GROOT" || v.weergave === "LIJST" || v.weergave === "KLEIN" ? v.weergave : "KLEIN",
      groeperen: v.groeperen === "GEEN" ? "GEEN" : "RESERVERING",
      sortering: SORTERINGEN.includes(v.sortering as Sortering) ? (v.sortering as Sortering) : "AANKOMST_NIEUW",
      velden: {
        RESERVERING: geldig(v.velden?.RESERVERING, STANDAARD_VELDEN.RESERVERING),
        GEEN: geldig(v.velden?.GEEN, STANDAARD_VELDEN.GEEN),
      },
    };
  } catch {
    return STANDAARD_VOORKEUR;
  }
}

export function bewaarVoorkeur(v: Voorkeur): void {
  try {
    window.localStorage.setItem(OPSLAG, JSON.stringify(v));
  } catch {
    // Geen opslag: dan geldt de keuze tot de pagina dichtgaat.
  }
}

export const SORTERINGEN: Sortering[] = ["AANKOMST_NIEUW", "AANKOMST_OUD", "VERTREK", "NAAM", "EIGENAAR", "KENNEL"];

const tekst = (x: string | null) => (x ?? "").toLocaleLowerCase("nl");

/** De volgorde van twee dieren; bij gelijke stand de naam, zodat de lijst niet verspringt. */
export function vergelijk(sortering: Sortering): (a: Regel, b: Regel) => number {
  const opNaam = (a: Regel, b: Regel) => tekst(a.dierNaam).localeCompare(tekst(b.dierNaam), "nl");
  const met = (f: (a: Regel, b: Regel) => number) => (a: Regel, b: Regel) => f(a, b) || opNaam(a, b);
  switch (sortering) {
    case "AANKOMST_NIEUW":
      return met((a, b) => b.van.localeCompare(a.van));
    case "AANKOMST_OUD":
      return met((a, b) => a.van.localeCompare(b.van));
    case "VERTREK":
      return met((a, b) => a.tot.localeCompare(b.tot));
    case "EIGENAAR":
      return met((a, b) => tekst(a.achternaam ?? a.klantNaam).localeCompare(tekst(b.achternaam ?? b.klantNaam), "nl"));
    case "KENNEL":
      // Nog niet ingedeeld komt achteraan: dat is werk, maar de ingedeelde dieren zoek je op hok.
      return met((a, b) => (a.kennel == null ? 1 : 0) - (b.kennel == null ? 1 : 0) || tekst(a.kennel).localeCompare(tekst(b.kennel), "nl", { numeric: true }));
    case "NAAM":
    default:
      return opNaam;
  }
}

/** De dieren van één reservering bij elkaar; de sleutel onderscheidt pension van dagopvang. */
export interface Groep {
  sleutel: string;
  regels: Regel[];
}

export function groepeer(regels: Regel[], sortering: Sortering): Groep[] {
  const groepen = new Map<string, Regel[]>();
  for (const r of regels) {
    const sleutel = `${r.soort}-${r.reserveringId ?? `los-${r.dierId}`}`;
    groepen.set(sleutel, [...(groepen.get(sleutel) ?? []), r]);
  }
  const volgorde = vergelijk(sortering);
  return [...groepen.entries()]
    .map(([sleutel, rs]) => ({ sleutel, regels: [...rs].sort(vergelijk("NAAM")) }))
    .sort((a, b) => volgorde(a.regels[0], b.regels[0]));
}

export type SoortGroep = "HOND" | "KAT" | "OVERIG";

export function soortGroep(diersoort: string | null): SoortGroep {
  const s = tekst(diersoort);
  if (s.startsWith("hond")) return "HOND";
  if (s.startsWith("kat") || s.startsWith("poes")) return "KAT";
  return "OVERIG";
}

/** Reu of teef bij een hond, kater of poes bij een kat; anders gewoon het geslacht. */
export function geslachtWoord(r: Pick<Regel, "geslacht" | "diersoort">, t: (k: string) => string): string | null {
  const g = tekst(r.geslacht);
  const man = g.startsWith("m") || g.startsWith("r") || g.startsWith("k");
  const vrouw = g.startsWith("v") || g.startsWith("t") || g.startsWith("p");
  if (!man && !vrouw) return null;
  const soort = soortGroep(r.diersoort);
  return t(`pensionOverzichten.geslacht.${soort}.${man ? "M" : "V"}`);
}

/** Welk tabblad een dier laat zien. */
export function inTab(r: Regel, tab: Tab): boolean {
  switch (tab) {
    case "aankomsten":
      return r.aankomst;
    case "vertrekken":
      return r.vertrek;
    case "reserveringen":
      return r.status === "GEPLAND";
    case "aanwezig":
    default:
      return r.status !== "GEPLAND";
  }
}
